import { cn } from "@repo/shared/utils";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { ChevronLeftIcon, PlusIcon, XIcon } from "lucide-react";
import { useContext, useId } from "react";
import { FieldMappingContext } from "./FieldMappingContext";
import type { LaserfichePutField } from "@repo/types";
import { SaveAllContext } from "./saveAllContext";

export interface FieldMappingProps {
  value: LaserfichePutField;
  onChange: (prevValue: LaserfichePutField, value: LaserfichePutField) => void;
  mode?: "add" | "delete";
  onButtonClick?: (value: LaserfichePutField) => void | Promise<void>;
}

export const LaserficheFieldMapping = ({
  value,
  onChange,
  mode,
  onButtonClick,
}: FieldMappingProps) => {
  const id = useId();

  const { disabled, mutableId, availableFields, availableSources, fieldMap } =
    useContext(FieldMappingContext)!;

  const boundChange = onChange.bind(null, value);

  const isEmpty = !value.fieldId || (!value.sourceKey && !value.constantValue);

  // use SaveAll for moving
  SaveAllContext.useAsChild(
    mode === "add"
      ? { order: 0, save: async () => await onButtonClick?.(value) }
      : null
  );

  const fieldData = fieldMap[value.fieldId];
  const isConstValue = value.sourceKey === "";

  return (
    <div className="w-full flex flex-row items-start gap-3">
      <div className="w-0 grow mt-1.5 flex flex-col gap-1">
        {mutableId ? (
          <SelectInput
            disabled={disabled}
            label="Laserfiche"
            labelStyle="italic text-sm opacity-50"
            containerStyle="mt-0"
            value={"" + (value.fieldId || "")}
            onValueChange={(newId) =>
              boundChange({ ...value, fieldId: +newId })
            }
            options={availableFields.map((field) => (
              <SelectItem key={field.id} value={"" + field.id}>
                {field.name}
                <br />
                <span className="text-xs opacity-50">{field.description}</span>
              </SelectItem>
            ))}
          />
        ) : (
          <div className="flex flex-col gap-y-1">
            <p className="italic text-sm opacity-50">Laserfiche</p>
            <p className="border border-gray-300 px-3 py-2 rounded-md w-full">
              {fieldData?.name ?? "<Unknown Field>"}
            </p>
          </div>
        )}
        <p className="text-xs opacity-60">Type: {fieldData?.type}</p>
        {fieldData?.type === "String" && fieldData?.length != null ? (
          <p className="text-xs opacity-60">Max Length: {fieldData.length}</p>
        ) : null}
        {fieldData?.constraint ? (
          <p className="text-xs opacity-60">
            Constraint: {fieldData.constraint}
          </p>
        ) : null}
      </div>

      <ChevronLeftIcon className="mt-9" />

      <SelectInput
        disabled={disabled}
        label="CiviDocs"
        labelStyle="italic text-sm opacity-50"
        containerStyle={cn("w-0 grow", isConstValue && "grow-[0.3]")}
        value={isConstValue ? "__other__" : value.sourceKey}
        onValueChange={(newValue) =>
          boundChange({
            ...value,
            sourceKey: newValue === "__other__" ? "" : newValue,
          })
        }
        options={[
          ...availableSources.map((source) => (
            <SelectItem key={source.key} value={source.key}>
              {source.label}
            </SelectItem>
          )),
          <SelectItem key="__other__" value="__other__">
            Other
          </SelectItem>,
        ]}
      />

      {isConstValue && (
        <Input
          id={"constant" + id}
          label="Constant Value"
          labelStyle="italic text-sm opacity-50"
          containerStyle="w-0 grow"
          disabled={disabled}
          value={value.constantValue ?? ""}
          onChange={(evt) =>
            boundChange({ ...value, constantValue: evt.target.value })
          }
        />
      )}

      {onButtonClick && mode && (
        <Button
          disabled={disabled}
          variant="ghost"
          className={cn(
            "w-8 h-8 p-0 flex justify-center items-center rounded-full mt-8",
            mode === "add" &&
              !isEmpty &&
              "bg-primary hover:bg-primary/90 text-white"
          )}
          onClick={() => onButtonClick(value)}
        >
          {mode === "add" ? (
            <PlusIcon className={cn("w-6 h-6", isEmpty && "opacity-80")} />
          ) : (
            <XIcon className="w-6 h-6" />
          )}
        </Button>
      )}
    </div>
  );
};
